import { apiSupplierContract } from '@montugroup/circuit-api-contracts';
import { supplierContract } from '@montugroup/contracts-warehousing-api';
import { initClient, tsRestFetchApi } from '@ts-rest/core';
import type { TsRestReactQueryHooksContainer } from '@ts-rest/react-query/v5';
import { initTsrReactQuery } from '@ts-rest/react-query/v5';
import type { FC, JSX, PropsWithChildren } from 'react';
import { createContext, useContext, useMemo } from 'react';
import { apiBaseUrl } from '~/constants/env';
import { Logger } from '~/lib/logger';
import { useAuth } from './AuthProvider';

const logger = new Logger('ApiProvider');

type ApiContextProps = {
  warehouseApi: {
    ReactQueryProvider: ({ children }: React.PropsWithChildren) => JSX.Element;
  } & TsRestReactQueryHooksContainer<
    typeof supplierContract,
    { baseUrl: string; baseHeaders: { 'Content-Type': string; Authorization?: string } }
  >;
  circuitApi: {
    query: {
      ReactQueryProvider: ({ children }: React.PropsWithChildren) => JSX.Element;
    } & TsRestReactQueryHooksContainer<
      typeof apiSupplierContract,
      { baseUrl: string; baseHeaders: { 'Content-Type': string; Authorization?: string } }
    >;
    client: ReturnType<typeof supplierApiClient>;
  };
};

const supplierApiClient = (token: string | null, logout: () => void) =>
  initClient(apiSupplierContract, {
    baseUrl: `${apiBaseUrl()}/circuit`,
    baseHeaders: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    api: async (args) => {
      return tsRestFetchApi(args).then((res) => {
        // We only want to potentially logout the user for requests that require authentication
        if (!token) {
          return res;
        }

        // If the request is unauthorized, the user will be redirected to the login page via the RequireAuth component
        if (res.status === 401) {
          logger.error('Unauthorized request');
          logout();
        }
        return res;
      });
    },
  });

const ApiContext = createContext<ApiContextProps | undefined>(undefined);

export const ApiProvider: FC<PropsWithChildren> = ({ children }) => {
  const { token, logout } = useAuth();

  const warehouseApi = useMemo(() => {
    return initTsrReactQuery(supplierContract, {
      baseUrl: `${apiBaseUrl()}/warehousing`,
      baseHeaders: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      api: async (args) => {
        return tsRestFetchApi(args).then((res) => {
          // We only want to potentially logout the user for requests that require authentication
          if (!token) {
            return res;
          }

          // If the request is unauthorized, the user will be redirected to the login page via the RequireAuth component
          if (res.status === 401) {
            logger.error('Unauthorized request');
            logout();
          }
          return res;
        });
      },
    });
  }, [logout, token]);

  const circuitApi = useMemo(() => {
    return {
      query: initTsrReactQuery(apiSupplierContract, {
        baseUrl: `${apiBaseUrl()}/circuit`,
        baseHeaders: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        api: async (args) => {
          return tsRestFetchApi(args).then((res) => {
            // We only want to potentially logout the user for requests that require authentication
            if (!token) {
              return res;
            }

            // If the request is unauthorized, the user will be redirected to the login page via the RequireAuth component
            if (res.status === 401) {
              logger.error('Unauthorized request');
              logout();
            }
            return res;
          });
        },
      }),

      /*
        Provide the ts-rest core client as well, since our POST /search endpoint only exposes
        useMutation in the above react query client which is more challenging to work with than just
        using a useQuery hook backed by the core ts-rest client
       */
      client: supplierApiClient(token, logout),
    };
  }, [logout, token]);

  return (
    <ApiContext.Provider value={{ warehouseApi, circuitApi }}>
      <warehouseApi.ReactQueryProvider>{children}</warehouseApi.ReactQueryProvider>
    </ApiContext.Provider>
  );
};

export const useApi = () => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApi must be used within an ApiProvider');
  }
  return context;
};
